import { z } from "zod";

/**
 * `MINIMUM-VEHICLE-PRICE` (founder D-1102-10; the architect's rulings of 2 November 2026): **one platform setting, the least price a vehicle may be listed or repriced at.** Hakili's own setting
 * (versioned, administration only under `parametrerPaiements`, audited), built on `MILEAGE-TOLERANCE`'s shape.
 *
 * - It applies when a price is CREATED or CHANGED, never on an ordinary edit: a listing already below it is not blocked and is never silently changed.
 * - Every amount that would become the vehicle's price obeys it, from ONE source: a new listing, the dealer's and the staff's price doors, and the price-gap decisions (align, propose, accept-counter, so
 *   a buyer's counter when accepted).
 * - Whole FCFA, 1 to 10 000 000, served with the setting. Effective the publication day. Nothing published is the served state `unconfigured`: today's « greater than zero » stands.
 *
 * **Codes, not words.** The console's sentence for `unconfigured` is the A-H architect's.
 */
export const MINIMUM_VEHICLE_PRICE_BOUNDS = { minAmount: 1, maxAmount: 10_000_000 } as const;

function buildSchemas(object: typeof z.strictObject) {
  const amount = z
    .number()
    .int()
    .min(MINIMUM_VEHICLE_PRICE_BOUNDS.minAmount)
    .max(MINIMUM_VEHICLE_PRICE_BOUNDS.maxAmount);
  const version = object({
    version: z.number().int().min(1),
    minimumPriceAmount: amount,
    effectiveFrom: z.string(),
    publishedAt: z.string(),
    publishedByName: z.string().nullable(),
  });
  const setting = object({
    state: z.enum(["configured", "unconfigured"]),
    minimumPriceAmount: amount.nullable(),
    version: z.number().int().min(1).nullable(),
    effectiveFrom: z.string().nullable(),
    publishedByName: z.string().nullable(),
    bounds: object({ minAmount: z.literal(1), maxAmount: z.literal(MINIMUM_VEHICLE_PRICE_BOUNDS.maxAmount) }),
    /** Newest first. */
    history: z.array(version),
  });
  const answer = object({ setting });
  /** One live listing priced below the floor now in force (arc B: the administration's list; the price is never rewritten). */
  const below = object({
    vehicleId: z.string(),
    stockReference: z.string().nullable(),
    title: z.string(),
    dealershipName: z.string().nullable(),
    priceAmount: z.number().int(),
    stage: z.string(),
    published: z.boolean(),
  });
  const belowFloor = object({
    state: z.enum(["configured", "unconfigured"]),
    floorAmount: amount.nullable(),
    /** The true count; `vehicles` is capped, cheapest first. */
    total: z.number().int().min(0),
    vehicles: z.array(below),
  });
  return { version, setting, answer, below, belowFloor };
}

const strict = buildSchemas(z.strictObject);
const loose = buildSchemas(z.looseObject);

export const minimumVehiclePriceVersionSchema = strict.version;
export const minimumVehiclePriceSettingSchema = strict.setting;
export const minimumVehiclePriceAnswerSchema = strict.answer;
/** The READ twin: what a client parses the live answer with. */
export const minimumVehiclePriceAnswerReadSchema = loose.answer;

export const minimumVehiclePriceBelowFloorAnswerSchema = strict.belowFloor;
export const minimumVehiclePriceBelowFloorAnswerReadSchema = loose.belowFloor;
export type MinimumVehiclePriceBelowFloorAnswer = z.infer<typeof minimumVehiclePriceBelowFloorAnswerSchema>;

export type MinimumVehiclePriceSetting = z.infer<typeof minimumVehiclePriceSettingSchema>;
export type MinimumVehiclePriceAnswer = z.infer<typeof minimumVehiclePriceAnswerSchema>;
